import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useDarkMode } from '../contexts/DarkModeContext';
import { useSettings } from '../contexts/SettingsContext';
import { Toast } from 'flowbite-react';
import { Check, Volume2, Play, X } from 'lucide-react';
import Select from 'react-select';
import { DeviceConnection } from '../components/settings/SimpleDashboardSettings/DeviceConnection';
import { DisplaySettings } from '../components/settings/SimpleDashboardSettings/DisplaySettings';
import { BackgroundSettings } from '../components/settings/SimpleDashboardSettings/BackgroundSettings';
import { AdhanLocationSettings } from '../components/settings/SimpleDashboardSettings/AdhanLocationSettings';
import { sectionStyles } from '../components/settings/SimpleDashboardSettings/styles';
import { useAdhanSettings } from '../components/settings/DashboardSettings/hooks/useAdhanSettings';
import { selectStyles } from '../components/settings/DashboardSettings/styles';
import { LocationSettings } from '../components/settings/DashboardSettings/components/LocationSettings';
import { SelectionView } from '../components/settings/DashboardSettings/components/SelectionView';
import { TuneView } from '../components/settings/DashboardSettings/components/TuneView';
import { calculationMethodOptions } from '../components/settings/DashboardSettings';

const AdhanClockPage = () => {
  const { isDarkMode } = useDarkMode();
  const { settings, updateSettings } = useSettings();
  const styles = sectionStyles(isDarkMode);

  // --- State replicated from SimpleDashboardSettings/index.js ---
  const isInitialMount = useRef(true);
  const settingsUpdateInProgress = useRef(false);
  const previousTemplateRef = useRef(null);

  const [selectedTemplate, setSelectedTemplate] = useState(() => {
    const template = settings.simpleDashboard?.templateId || null;
    previousTemplateRef.current = template;
    return template;
  });

  const [showImageTemplates, setShowImageTemplates] = useState(true);

  const [selectedImages, setSelectedImages] = useState(() => {
    return settings.simpleDashboard?.backgroundImages || [];
  });

  const [showToast, setShowToast] = useState(false);
  const [error, setError] = useState('');
  const [toastMessage, setToastMessage] = useState('Settings saved successfully');

  // --- Prayer time settings state (from DashboardSettings/index.js) ---
  const [isMobileView, setIsMobileView] = useState(window.innerWidth < 768);
  const [showCalculationSheet, setShowCalculationSheet] = useState(false);
  const [showTuneModal, setShowTuneModal] = useState(false);
  const [tunedTimes, setTunedTimes] = useState({
    Fajr: 0,
    Dhuhr: 0,
    Asr: 0,
    Maghrib: 0,
    Isha: 0
  });
  const [calculationMethod, setCalculationMethod] = useState(settings.calculationMethod || 1);

  useEffect(() => {
    const handleResize = () => {
      setIsMobileView(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    setTunedTimes(settings.tunedTimes || {
      Fajr: 0,
      Dhuhr: 0,
      Asr: 0,
      Maghrib: 0,
      Isha: 0
    });
  }, [settings.tunedTimes]);

  const handleTuneChange = (prayer, value) => {
    if (prayer === null && typeof value === 'object') {
      setTunedTimes(value);
    } else {
      setTunedTimes(prev => ({
        ...prev,
        [prayer]: parseInt(value)
      }));
    }
  };

  const saveTunedTimes = () => {
    const formattedTunedTimes = {};
    Object.keys(tunedTimes).forEach(prayer => {
      formattedTunedTimes[prayer] = parseInt(tunedTimes[prayer] || 0);
    });

    const apiTunedTimes = {
      ...formattedTunedTimes,
      Imsak: 0,
      Sunrise: 0,
      Sunset: 0,
      Midnight: 0
    };

    updateSettings({
      tunedTimes: formattedTunedTimes,
      apiTunedTimes
    });
  };

  const resetTunedTimes = () => {
    const resetTimes = {
      Fajr: 0,
      Dhuhr: 0,
      Asr: 0,
      Maghrib: 0,
      Isha: 0
    };

    const resetApiTimes = {
      ...resetTimes,
      Imsak: 0,
      Sunrise: 0,
      Sunset: 0,
      Midnight: 0
    };

    setTunedTimes(resetTimes);
    updateSettings({
      tunedTimes: resetTimes,
      apiTunedTimes: resetApiTimes
    });
  };

  const handleCalculationMethodChange = (option) => {
    const newMethod = parseInt(option.value);
    setCalculationMethod(newMethod);
    updateSettings({ calculationMethod: newMethod });
  };

  // --- SimpleDashboard template sync ---
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    if (settingsUpdateInProgress.current) {
      settingsUpdateInProgress.current = false;
      return;
    }
    const newTemplate = settings.simpleDashboard?.templateId || null;
    if (newTemplate !== selectedTemplate && newTemplate !== previousTemplateRef.current) {
      setSelectedTemplate(newTemplate);
      previousTemplateRef.current = newTemplate;
    }
  }, [settings.simpleDashboard, selectedTemplate]);

  const handleSaveSettings = useCallback((newSettings) => {
    settingsUpdateInProgress.current = true;
    updateSettings({
      simpleDashboard: {
        ...settings.simpleDashboard,
        ...newSettings,
      },
    });
    if (newSettings.templateId !== undefined) {
      previousTemplateRef.current = newSettings.templateId;
    }
    setToastMessage('Settings saved successfully');
    setShowToast(true);
    setTimeout(() => setShowToast(false), 3000);
  }, [settings.simpleDashboard, updateSettings]);

  const handleSuccess = useCallback((message) => {
    setToastMessage(message || 'Operation completed successfully');
    setShowToast(true);
    setTimeout(() => setShowToast(false), 3000);
  }, []);

  const handleError = useCallback((errorMessage) => {
    setError(errorMessage);
    setTimeout(() => setError(''), 3000);
  }, []);

  // --- Adhan reciter (inline, using hook directly) ---
  const {
    selectedAdhan,
    adhanOptions,
    isPlayingAdhan,
    handleAdhanChange,
    handlePlayAdhan,
  } = useAdhanSettings();

  // --- Mobile sheet helpers ---
  const isSheetOpen = showCalculationSheet || showTuneModal;

  const pageContent = (
    <div className={`min-h-screen px-4 py-8 md:px-6 pb-24 ${isDarkMode ? 'bg-[#0A0A0A]' : 'bg-gray-50'}`}>
      <div className="max-w-3xl mx-auto">

        {/* Hero header */}
        <div className="mb-12 text-center">
          <h1 className={`text-3xl md:text-4xl font-bold tracking-tight ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            Adhan Clock
          </h1>
          <p className={`mt-2 text-base ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            Link and manage your Adhan Clock settings
          </p>
        </div>

        <div className="space-y-12">

          {/* 1. Device Connection */}
          <DeviceConnection onError={handleError} />

          {/* 2. Prayer Location & Calculation Settings */}
          <LocationSettings
            isDarkMode={isDarkMode}
            onOpenCalculation={() => setShowCalculationSheet(true)}
            onOpenTune={() => setShowTuneModal(true)}
          />

          {/* 3. Adhan Location Settings (clock-specific custom location) */}
          <AdhanLocationSettings onSave={handleSaveSettings} />

          {/* 4. Adhan Reciter (inline section) */}
          <div className={styles.container}>
            <div className={styles.header}>
              <Volume2 className={styles.headerIcon} />
              <h3 className={styles.headerText}>Adhan Reciter</h3>
            </div>
            <div className={styles.card}>
              <div className="flex flex-col sm:flex-row items-stretch gap-2">
                <div className="flex-1">
                  <Select
                    options={adhanOptions}
                    value={adhanOptions.find(option => option.value === selectedAdhan)}
                    onChange={(option) => handleAdhanChange(option.value)}
                    styles={selectStyles(isDarkMode)}
                    placeholder="Select Reciter"
                    isSearchable={false}
                  />
                </div>
                <button
                  onClick={handlePlayAdhan}
                  disabled={!selectedAdhan}
                  className={`w-full sm:w-[120px] px-4 py-2.5 rounded-xl transition-all duration-300 ${
                    isPlayingAdhan
                      ? isDarkMode
                        ? 'bg-red-600/20 text-red-400 hover:bg-red-600/30'
                        : 'bg-red-600 text-white hover:bg-red-700'
                      : isDarkMode
                        ? 'bg-emerald-600/20 text-emerald-400 hover:bg-emerald-600/30'
                        : 'bg-emerald-600 text-white hover:bg-emerald-700'
                  } shadow-sm hover:shadow-md flex items-center justify-center gap-2`}
                >
                  {isPlayingAdhan ? (
                    <>
                      <X size={18} />
                      <span>Stop</span>
                    </>
                  ) : (
                    <>
                      <Play size={18} />
                      <span>Play</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* 5. Display Settings */}
          <DisplaySettings
            showImageTemplates={showImageTemplates}
            setShowImageTemplates={setShowImageTemplates}
            selectedTemplate={selectedTemplate}
            setSelectedTemplate={setSelectedTemplate}
            onSave={handleSaveSettings}
            settings={settings}
          />

          {/* 6. Background Settings (conditional) */}
          {selectedTemplate && showImageTemplates && (
            <BackgroundSettings
              selectedTemplate={selectedTemplate}
              selectedImages={selectedImages}
              setSelectedImages={setSelectedImages}
              onSave={handleSaveSettings}
              onSuccess={handleSuccess}
              onError={handleError}
            />
          )}
        </div>

        {/* Toast Notification */}
        {showToast && (
          <div className="fixed bottom-6 right-5 z-50">
            <Toast>
              <div className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-emerald-100 text-emerald-500">
                <Check className="h-5 w-5" />
              </div>
              <div className="ml-3 text-sm font-normal">{toastMessage}</div>
            </Toast>
          </div>
        )}

        {/* Error Toast */}
        {error && (
          <div className="fixed bottom-6 right-5 z-50 bg-red-500 text-white px-4 py-2 rounded-lg shadow-lg">
            {error}
          </div>
        )}
      </div>
    </div>
  );

  // Mobile: wrap content with sheet animations
  if (isMobileView) {
    return (
      <div className="relative">
        <motion.div
          initial={false}
          animate={{ x: isSheetOpen ? '-15%' : 0 }}
          transition={{ type: 'spring', bounce: 0, duration: 0.4 }}
          className="relative"
        >
          {pageContent}
        </motion.div>

        <AnimatePresence>
          {showCalculationSheet && (
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', bounce: 0, duration: 0.4 }}
              className={`fixed inset-0 z-50 ${isDarkMode ? 'bg-[#0A0A0A]' : 'bg-gray-50'}`}
            >
              <SelectionView
                show={true}
                onClose={() => setShowCalculationSheet(false)}
                title="Method"
                options={calculationMethodOptions}
                selectedValue={calculationMethodOptions.find(opt => opt.value === calculationMethod)}
                onSelect={handleCalculationMethodChange}
                isDarkMode={isDarkMode}
              />
            </motion.div>
          )}

          {showTuneModal && (
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', bounce: 0, duration: 0.4 }}
              className="fixed inset-0 z-50"
            >
              <TuneView
                show={true}
                onClose={() => setShowTuneModal(false)}
                tunedTimes={tunedTimes}
                onTuneChange={handleTuneChange}
                onSave={saveTunedTimes}
                onReset={resetTunedTimes}
                isDarkMode={isDarkMode}
              />
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  }

  // Desktop: page content + inline modals (sheets still work via MobilePageTransition)
  return pageContent;
};

export default AdhanClockPage;
